import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Lightbulb, ShieldCheck, Users, TrendingUp, Scale, Hourglass } from "lucide-react";
import SEOMeta from "../components/SEOMeta";
import { Reveal, Chapter, MaskedLine } from "../components/Reveal";
import { useContent } from "../lib/content";

const VALUE_ICONS = [Lightbulb, ShieldCheck, Users, TrendingUp, Scale, Hourglass];

export default function About() {
  const content = useContent();

  return (
    <div>
      <SEOMeta
        title="About Us | Touson Ventures (OPC) Private Limited"
        description="Learn about Touson Ventures — our vision, mission and values. A diversified Indian business company based in Pune, building sustainable, customer-focused businesses."
      />

      <section className="pt-44 pb-20 sm:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.07),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="font-mono text-[10px] sm:text-xs tracking-[0.35em] text-gold mb-8"
            data-testid="about-eyebrow"
          >
            ABOUT US
          </motion.p>
          <h1 className="font-display tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl" data-testid="about-headline">
            <MaskedLine delay={0.15}>A serious, modern company</MaskedLine>
            <MaskedLine delay={0.32} className="gold-gradient-text italic">with a long-term vision.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-2xl text-white/65 text-base sm:text-lg leading-relaxed"
            data-testid="about-intro"
          >
            {content.about.intro}
          </motion.p>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full border border-white/10 bg-white/[0.03] backdrop-blur-xl p-10 sm:p-14 hover:border-gold/40 transition-colors duration-500" data-testid="about-vision-card">
              <p className="font-mono text-[10px] tracking-[0.35em] text-gold uppercase mb-8">Our Vision</p>
              <p className="font-display text-2xl sm:text-3xl text-white leading-snug" data-testid="about-vision-text">
                {content.about.vision}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full border border-white/10 bg-white/[0.03] backdrop-blur-xl p-10 sm:p-14 hover:border-gold/40 transition-colors duration-500" data-testid="about-mission-card">
              <p className="font-mono text-[10px] tracking-[0.35em] text-gold uppercase mb-8">Our Mission</p>
              <p className="font-display text-2xl sm:text-3xl text-white leading-snug" data-testid="about-mission-text">
                {content.about.mission}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <Chapter num="01" title="Our Values" testId="about-values-chapter" />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {content.about.values.map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <Reveal key={v.title} delay={i * 0.06}>
                  <div
                    data-testid={`about-value-${i}`}
                    className="h-full bg-navy p-8 sm:p-10 hover:bg-navy-700 transition-colors duration-500 group"
                  >
                    <Icon size={22} className="text-gold mb-8" strokeWidth={1.5} />
                    <h3 className="font-display text-xl text-white mb-3 group-hover:text-gold transition-colors duration-300">
                      {v.title}
                    </h3>
                    <p className="text-sm text-white/55 leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <div className="border border-white/10 p-10 sm:p-16 flex flex-col lg:flex-row lg:items-center justify-between gap-10 relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(212,175,55,0.06),transparent_55%)]" />
              <div className="relative">
                <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">See what we are building.</h2>
                <p className="mt-4 text-white/55 max-w-lg">
                  Explore our current ventures and the opportunities we are developing for the future.
                </p>
              </div>
              <Link
                to="/businesses"
                data-testid="about-cta-button"
                className="relative group inline-flex items-center gap-3 bg-gold text-navy font-medium text-sm px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-300 shrink-0"
              >
                Our Businesses
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
